import { Mermaid } from "../../components/Mermaid";

const ARCHITECTURE = `flowchart LR
  Cand["Candidate App<br/>React + Supabase"]
  HR["HR App<br/>React + Clerk"]
  Supa[("Supabase<br/>Postgres + Storage + Auth")]
  N8N["n8n<br/>workflow engine"]
  Edge["Supabase Edge Functions<br/>Deno"]
  OpenAI["OpenAI<br/>GPT-4o + Whisper"]
  Gemini["Google Gemini<br/>Vision"]
  Apify["Apify<br/>scrapers"]
  GCal["Google Calendar + Gmail"]

  Cand -- "webhooks" --> N8N
  Cand -- "voice / chat" --> Edge
  HR --> Supa
  Cand --> Supa
  N8N --> Supa
  N8N --> OpenAI
  N8N --> Gemini
  N8N --> Apify
  Edge --> OpenAI
  Edge --> GCal
  Edge --> Supa`;

const JOURNEY = `flowchart TD
  A["1. Resume upload"] --> B["2. AI interview (voice)"]
  B --> C["3. DISC assessment"]
  C --> D["4. Graphology"]
  D --> E["5. Instagram scraping"]
  E -. "auto-triggers" .-> F["6. Post analysis (Gemini)"]
  F --> G["7. Interview scheduling"]
  G --> H["8. Stage notifications"]`;

const RESUME_FLOW = `flowchart LR
  W["Webhook: resume PDF"] --> P["Extract text"]
  P --> G1["GPT-4o: structured extraction"]
  G1 -- "low confidence" --> G2["Gemini Vision: fallback"]
  G1 --> S[("candidate_analysis")]
  G2 --> S`;

const INTERVIEW_FLOW = `sequenceDiagram
  participant C as Candidate
  participant E as Edge Function
  participant W as Whisper (OpenAI)
  participant N as GPT-4o-mini "NIC"
  participant DB as Supabase

  C->>E: Record answer (audio)
  E->>W: Transcribe (pt)
  W-->>E: Transcript
  E->>N: Question + transcript
  N-->>E: save_response(score, feedback)
  E->>DB: Insert candidate_responses
  E->>C: Next question (5 total)`;

const DISC_FLOW = `flowchart LR
  Q["40 questions, 3 stages"] --> Calc["Compute D / I / S / C profile"]
  Calc --> DB[("disc_results")]`;

const GRAPHOLOGY_FLOW = `flowchart LR
  Up["Upload handwriting photo"] --> DL["Download image"]
  DL --> GV["Gemini Vision:<br/>pressure, slant, spacing"]
  GV --> P["Parse JSON"]
  P --> DB[("graphology_results")]`;

const SOCIAL_FLOW = `flowchart TD
  W1["Webhook: Instagram handle"] --> AP["Apify: Instagram Scraper"]
  AP --> Cls["Classify posts, reels, carousels"]
  Cls --> St["Upload media to Supabase Storage"]
  St --> DB1[("candidate_social_data")]
  DB1 -. "auto-triggers" .-> W2["Post-analysis workflow"]
  W2 --> GVL["Gemini Vision: batch image analysis"]
  GVL --> DB2[("candidate_social_analysis")]

  JD["Job requirements"] --> XR["LinkedIn cross-reference engine"]
  CV["CV data"] --> XR
  LI["Apify: LinkedIn scraper"] --> XR
  XR --> Score["Keyword match score +<br/>CV/LinkedIn inconsistency flags"]`;

export function RecrutaCISDeepDive() {
  return (
    <div>
      <div className="pf-deepdive-head">
        <span className="eyebrow">System architecture</span>
        <h2>How RecrutaCIS actually works.</h2>
        <p>
          RecrutaCIS runs on two React apps sharing one Supabase project, with an n8n instance doing
          the AI orchestration for most assessment steps and a pair of Supabase Edge Functions handling
          the parts that needed lower latency — most notably the voice interview.
        </p>
      </div>

      <div className="pf-deepdive-block">
        <h3>High-level architecture</h3>
        <p>
          The candidate app and the HR app never talk to each other directly — both read and write the
          same Postgres database. Every AI-driven step (resume parsing, interview scoring, graphology,
          social analysis) is its own n8n workflow triggered by a webhook, calling out to OpenAI, Gemini
          or Apify and writing the result straight back to Supabase.
        </p>
        <Mermaid chart={ARCHITECTURE} caption="Two frontends, one Supabase project, n8n + Edge Functions as the AI orchestration layer." />
      </div>

      <div className="pf-deepdive-block">
        <h3>Candidate journey</h3>
        <p>
          A candidate moves through eight stages end to end. Step 6 isn't triggered by the frontend at all —
          the Instagram-scraping workflow calls the post-analysis workflow internally once scraping finishes,
          an n8n-to-n8n chain rather than a frontend-orchestrated one.
        </p>
        <div className="pf-journey-steps">
          {[
            ["01", "Resume", "PDF upload, parsed and structured by GPT-4o."],
            ["02", "AI Interview", "Voice Q&A, transcribed and scored live."],
            ["03", "DISC", "40-question behavioral profile."],
            ["04", "Graphology", "Handwriting analyzed by Gemini Vision."],
            ["05", "Instagram", "Public activity scraped via Apify."],
            ["06", "Post analysis", "Gemini reads tone, interests, fit."],
            ["07", "Scheduling", "Google Calendar + Gmail invite."],
            ["08", "Notifications", "Stage-change emails, both sides."],
          ].map(([n, t, d]) => (
            <div className="pf-journey-step" key={n}>
              <span className="pf-journey-step-index">{n}</span>
              <h4>{t}</h4>
              <p>{d}</p>
            </div>
          ))}
        </div>
        <Mermaid chart={JOURNEY} caption="Steps 5→6 chain automatically inside n8n; everything else is triggered from the candidate app." />
      </div>

      <div className="pf-deepdive-block">
        <h3>Step 1 — Resume analysis</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">n8n workflow</span>
            <span className="pf-workflow-tag">GPT-4o</span>
            <span className="pf-workflow-tag">Gemini fallback</span>
          </div>
          <p>
            The resume PDF is parsed and handed to GPT-4o for structured extraction — name, experience,
            skills, education. If extraction confidence is low, the workflow falls back to Gemini Vision
            instead of failing outright. This try-primary-then-fallback pattern shows up in several
            workflows here, not just this one — a deliberate reliability decision, not a one-off.
          </p>
          <Mermaid chart={RESUME_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>Step 2 — AI interview</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">Supabase Edge Function</span>
            <span className="pf-workflow-tag">Whisper</span>
            <span className="pf-workflow-tag">GPT-4o-mini</span>
          </div>
          <p>
            This step used to run on n8n webhooks too — three earlier workflows were retired specifically
            because the voice loop needed lower latency than a webhook round-trip could give. It now runs
            on Supabase Edge Functions: the candidate records an audio answer, Whisper transcribes it in
            Portuguese, and an agent prompted as "NIC" asks five job-specific questions, scoring each one
            0–100 through an OpenAI function call. The scoring rubric lives directly in the system prompt —
            an editable document, not a black-box model.
          </p>
          <Mermaid chart={INTERVIEW_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>Step 3 — DISC assessment</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">n8n workflow</span>
            <span className="pf-workflow-tag">Behavioral profiling</span>
          </div>
          <p>
            A separate 40-question assessment across three stages computes a standard D/I/S/C behavioral
            profile — distinct from the technical-skills quiz elsewhere in the platform, not the same thing
            reused twice.
          </p>
          <Mermaid chart={DISC_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>Step 4 — Graphology</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">n8n workflow</span>
            <span className="pf-workflow-tag">Gemini Vision</span>
          </div>
          <p>
            An uncommon feature for a recruitment platform: the candidate photographs a handwritten letter,
            and Gemini Vision analyzes pressure, slant, size, spacing and margins to produce a personality
            report — a real production step, not a novelty demo.
          </p>
          <Mermaid chart={GRAPHOLOGY_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>Step 5–6 — Social media analysis</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">Apify</span>
            <span className="pf-workflow-tag">Gemini Vision</span>
            <span className="pf-workflow-tag">HR-side enrichment</span>
          </div>
          <p>
            Instagram scraping and post analysis are two n8n workflows chained automatically. Separately,
            on the HR side, a LinkedIn cross-reference engine compares job requirements, CV data and
            Apify-scraped LinkedIn data to compute a keyword-match score and flag inconsistencies — title
            mismatches, skill gaps, timeline gaps — the most sophisticated single piece of logic in the
            system, closer to fraud-detection than simple scraping.
          </p>
          <Mermaid chart={SOCIAL_FLOW} />
        </div>
      </div>
    </div>
  );
}
